import {
  Component,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { rxResource, toObservable } from '@angular/core/rxjs-interop';
import {
  PEF_ADAPTER,
  PefAdapter,
  PefApiData,
  PefData,
  PefEditor,
  PefLoadResult,
  flattenPefApiData,
  structurePefApiData,
} from '@sportello-balneari/pef-editor';
import {
  DocumentUploadComponent,
  SidebarNavClickEvent,
  SidebarNavComponent,
  SidebarNavItem,
  type DocumentAttachment,
} from '@sportello-balneari/shared-components';
import {
  Observable,
  catchError,
  filter,
  map,
  merge,
  of,
  skip,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { CompilingApiService } from '../../../api/compiling-api.service';
import { AnswerAttachmentsService } from '../../../services/compiling/state/answer-attachments.service';
import { PefStateService } from '../../../services/compiling/state/pef-state.service';
import { TenderContextService } from '../../../services/compiling/state/tender-context.service';
import { AnswerPermissionsService } from '../../../services/compiling/state/answer-permissions.service';
import { EnrichedSlotMetaComponent } from '../enriched-slot-meta.component';
import {
  requirementsToSlots,
  type EnrichedSlot,
} from '../requirements-to-slots';
import {
  deleteAttachmentFn,
  migrateUnknownAttachments,
  reindexAttachmentFn,
  uploadAndIndexFn,
} from '../utils';
import {
  CANONE_PEF_BASE_KEY,
  EnvelopeCEconomicOfferComponent,
} from './envelope-c-economic-offer.component';
import { PefReportComponent } from './pef-report.component';
import { PefSummaryComponent } from './pef-summary.component';
import {
  MOCK_END_YEAR,
  MOCK_PEF_DATA,
  MOCK_START_YEAR,
} from './pef-summary.mock';
import {
  PefYearRangePickerComponent,
  YearRangeApplyEvent,
} from './pef-year-range-picker.component';
import { pefYearSpan } from './pef-year-span';

const FALLBACK_MIN_YEAR_SPAN = 3;
const FALLBACK_MAX_YEAR_SPAN = 20;
const SUMMARY_ID = 'summary';
const ECONOMIC_OFFER_ID = 'economic-offer';
const DOCUMENTS_ID = 'documents';
const PEF_SHEET_PREFIX = 'pef-sheet-';
const PEF_REPORT_ID = 'pef-report';

// TODO: set to false once the summary has been reviewed visually.
const USE_MOCK_SUMMARY = false;

@Component({
  selector: 'lib-envelope-c',
  template: `
    <div
      class="mx-auto grid h-full w-full max-w-screen-lg grid-cols-[20rem_1fr] items-start gap-4"
    >
      <!-- Sidebar -->
      <aside
        class="sticky top-[120.250px] flex max-h-[calc(100vh-220px)] flex-shrink-0 flex-col overflow-y-auto rounded-2xl border border-gray-200 bg-white"
      >
        <!-- Year range picker -->
        <lib-pef-year-range-picker
          [startYear]="pefState.pefStartYear()"
          [endYear]="pefState.pefEndYear()"
          [minYearSpan]="minYearSpan()"
          [maxYearSpan]="maxYearSpan()"
          [minYear]="MIN_YEAR"
          [maxYear]="MAX_YEAR"
          [pefVersion]="pefState.pefVersion()"
          [readonly]="!permissions.canEditEconomicOffer()"
          (apply)="onYearRangeApply($event)"
        />

        <hr class="my-2 border-gray-100" />

        <lib-sidebar-nav
          [items]="staticNavItems()"
          [activeId]="activeNavId()"
          (itemClick)="onNavClick($event)"
        />

        @if (schemaResource.hasValue() && schemaResource.value() !== null) {
          <hr class="my-2 border-gray-100" />
          <span class="sidebar-section-label">PEF</span>
          <lib-sidebar-nav
            [items]="pefNavItems()"
            [activeId]="activeNavId()"
            (itemClick)="onNavClick($event)"
          />
        } @else if (schemaResource.error()) {
          <hr class="my-2 border-gray-100" />
          <p class="px-4 py-2 text-sm text-red-600">
            Impossibile caricare lo schema PEF.
          </p>
        }
      </aside>

      <!-- Main content -->
      @if (activeView() === 'summary') {
        <lib-pef-summary
          class="min-w-0 flex-1"
          [data]="summaryData()"
          [startYear]="summaryStartYear()"
          [endYear]="summaryEndYear()"
        />
      } @else if (activeView() === 'economic-offer' && mode() === 'full') {
        <lib-envelope-c-economic-offer
          class="min-w-0 flex-1"
          [pefData]="pefData()"
          [pefStartYear]="pefState.pefStartYear()"
          [pefEndYear]="pefState.pefEndYear()"
          [recalculating]="pefDataResource.isLoading()"
          [readonly]="!permissions.canEditEconomicOffer()"
          (canoneApply)="onCanoneApply($event)"
        />
      } @else if (activeView() === 'documents') {
        <div class="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto">
          @for (item of enrichedDocumentSlots(); track item.slot.id) {
            <lib-document-upload
              [slot]="item.slot"
              [uploadFn]="uploadFn(item.slot.id)"
              [deleteFn]="deleteFn"
              [reindexFn]="reindexFn"
              [readonly]="!permissions.canEditEconomicOffer()"
              [value]="getSlot(item.slot.id)"
              (valueChange)="setSlot(item.slot.id, $event)"
            >
              <lib-enriched-slot-meta [item]="item" />
            </lib-document-upload>
          }
        </div>
      } @else if (activeView() === 'pef-report') {
        <lib-pef-report class="min-w-0 flex-1" />
      } @else if (schemaResource.error()) {
        <div
          class="flex min-w-0 flex-1 items-center justify-center rounded-2xl border border-red-200 bg-red-50 p-8 text-red-700"
        >
          <span>Impossibile caricare lo schema PEF.</span>
        </div>
      } @else if (
        schemaResource.hasValue() && schemaResource.value() !== null
      ) {
        <lib-pef-editor
          #pefEditorRef
          [schema]="schemaResource.value()!"
          [(activeSheet)]="activeSheet"
          [readonly]="!permissions.canEditEconomicOffer()"
          (inputsChange)="onEditorInputsChange($event)"
          class="pef-editor-panel min-w-0 flex-1"
        />
      }
    </div>
  `,
  styleUrl: './envelope-c.component.css',
  imports: [
    PefEditor,
    PefReportComponent,
    PefSummaryComponent,
    EnvelopeCEconomicOfferComponent,
    SidebarNavComponent,
    DocumentUploadComponent,
    PefYearRangePickerComponent,
    EnrichedSlotMetaComponent,
  ],
  providers: [
    {
      provide: PEF_ADAPTER,
      useExisting: EnvelopeCComponent,
    },
  ],
})
export class EnvelopeCComponent implements PefAdapter {
  readonly mode = input<'full' | 'detached'>('full');

  protected readonly compilingApiService = inject(CompilingApiService);
  protected readonly pefState = inject(PefStateService);
  protected readonly permissions = inject(AnswerPermissionsService);
  private readonly tenderContext = inject(TenderContextService);
  private readonly attachments = inject(AnswerAttachmentsService);

  @ViewChild('pefEditorRef') private readonly pefEditor?: PefEditor;

  readonly activeSheet = signal(0);
  readonly activeView = signal<
    'summary' | 'economic-offer' | 'pef' | 'documents' | 'pef-report'
  >('summary');

  protected readonly MIN_YEAR = 2000;
  protected readonly MAX_YEAR = 2100;

  protected readonly minYearSpan = computed(() => {
    const lot = this.tenderContext.tenderLotDetails();
    if (lot?.concessionPeriodYears != null) return lot.concessionPeriodYears;
    return (
      this.tenderContext.tenderInfo()?.concessionPeriodYears ??
      FALLBACK_MIN_YEAR_SPAN
    );
  });

  protected readonly maxYearSpan = computed(() => {
    const lot = this.tenderContext.tenderLotDetails();
    if (lot?.concessionPeriodMaxYears != null)
      return lot.concessionPeriodMaxYears;
    return (
      this.tenderContext.tenderInfo()?.concessionPeriodMaxYears ??
      FALLBACK_MAX_YEAR_SPAN
    );
  });

  protected readonly summaryData = computed(() =>
    USE_MOCK_SUMMARY ? MOCK_PEF_DATA : this.pefData(),
  );
  protected readonly summaryStartYear = computed(() =>
    USE_MOCK_SUMMARY
      ? MOCK_START_YEAR
      : (this.pefState.pefStartYear() ?? this.MIN_YEAR),
  );
  protected readonly summaryEndYear = computed(() =>
    USE_MOCK_SUMMARY
      ? MOCK_END_YEAR
      : (this.pefState.pefEndYear() ?? this.MAX_YEAR),
  );

  private static readonly DEFAULT_ENRICHED_SLOTS: EnrichedSlot[] = [
    {
      slot: { id: 'altri-documenti', label: 'Altri documenti', multiple: true },
      members: [],
    },
  ];

  protected readonly enrichedDocumentSlots = computed<EnrichedSlot[]>(() => {
    const tenderInfo = this.tenderContext.tenderInfo();
    const envelopeC = tenderInfo?.extractedRequirements?.envelopeC;
    if (!envelopeC?.length) return EnvelopeCComponent.DEFAULT_ENRICHED_SLOTS;

    return [
      ...requirementsToSlots(envelopeC, tenderInfo?.attachments ?? []),
      {
        slot: {
          id: 'altri-documenti',
          label: 'Altri documenti',
          multiple: true,
        },
        members: [],
      },
    ];
  });

  protected readonly uploadFn = uploadAndIndexFn(
    this.compilingApiService,
    'ENVELOPE_C',
    () => this.tenderContext.tenderFormAnswer()?.id ?? '',
  );

  protected readonly deleteFn = deleteAttachmentFn(this.compilingApiService);
  protected readonly reindexFn = reindexAttachmentFn(this.compilingApiService);

  private readonly knownSlotIds = computed(
    () => new Set(this.enrichedDocumentSlots().map((item) => item.slot.id)),
  );

  protected getSlot(id: string): DocumentAttachment[] {
    return this.attachments.envelopeCAttachments()[id] ?? [];
  }

  protected setSlot(id: string, files: DocumentAttachment[]): void {
    this.attachments.envelopeCAttachments.update((v) => ({
      ...v,
      [id]: files,
    }));
  }

  protected readonly attachmentCount = computed(() =>
    Object.values(this.attachments.envelopeCAttachments()).reduce(
      (sum, files) => sum + files.length,
      0,
    ),
  );

  protected readonly activeNavId = computed(() => {
    if (this.activeView() === 'summary') return SUMMARY_ID;
    if (this.activeView() === 'economic-offer') return ECONOMIC_OFFER_ID;
    if (this.activeView() === 'documents') return DOCUMENTS_ID;
    if (this.activeView() === 'pef-report') return PEF_REPORT_ID;
    return `${PEF_SHEET_PREFIX}${this.activeSheet()}`;
  });

  protected readonly staticNavItems = computed<SidebarNavItem[]>(() => {
    const items: SidebarNavItem[] = [
      {
        id: SUMMARY_ID,
        label: 'Riepilogo',
        icon: 'pi pi-chart-bar',
      },
    ];
    if (this.mode() === 'full') {
      items.push({
        id: ECONOMIC_OFFER_ID,
        label: 'Offerta',
        icon: 'pi pi-euro',
      });
    }
    items.push(
      {
        id: DOCUMENTS_ID,
        label: 'Documenti',
        icon: 'pi pi-file',
        badge: this.attachmentCount() || undefined,
      },
      { id: PEF_REPORT_ID, label: 'Report', icon: 'pi pi-file-export' },
    );
    return items;
  });

  protected readonly pefNavItems = computed<SidebarNavItem[]>(() => {
    const schema = this.schemaResource.value();
    if (!schema) return [];

    const groups = schema.sheetGroups ?? [];
    if (groups.length === 0) {
      return schema.sheets.map((sheet, i) => ({
        id: `${PEF_SHEET_PREFIX}${i}`,
        label: sheet.label,
      }));
    }

    const groupMap = new Map(
      groups.map((g) => [
        g.id,
        {
          id: `pef-group-${g.id}`,
          label: g.label,
          selectable: false as const,
          children: [] as { id: string; label: string }[],
        },
      ]),
    );
    const ungrouped: SidebarNavItem[] = [];

    schema.sheets.forEach((sheet, i) => {
      const entry = { id: `${PEF_SHEET_PREFIX}${i}`, label: sheet.label };
      const group = sheet.sheetGroup
        ? groupMap.get(sheet.sheetGroup)
        : undefined;
      if (group) {
        group.children.push(entry);
      } else {
        ungrouped.push(entry);
      }
    });

    const grouped = [...groupMap.values()].filter((g) => g.children.length > 0);
    return [...ungrouped, ...grouped];
  });

  private readonly pefQuery = computed(() => ({
    tenderFormAnswerId: this.tenderContext.tenderFormAnswer()?.id,
    startYear: this.pefState.pefStartYear(),
    endYear: this.pefState.pefEndYear(),
    minYearSpan: this.minYearSpan(),
    maxYearSpan: this.maxYearSpan(),
    pefVersion: this.pefState.pefVersion(),
  }));

  readonly schemaResource = rxResource({
    params: () => ({
      tenderFormAnswerId: this.pefQuery().tenderFormAnswerId,
      startYear: this.pefQuery().startYear,
      endYear: this.pefQuery().endYear,
      minYearSpan: this.pefQuery().minYearSpan,
      maxYearSpan: this.pefQuery().maxYearSpan,
      pefVersion: this.pefQuery().pefVersion,
    }),
    stream: ({ params }) => {
      if (!params.tenderFormAnswerId || !params.startYear || !params.endYear) {
        return of(null);
      }
      const yearCount = pefYearSpan(
        params.startYear,
        params.endYear,
        params.pefVersion,
      );
      if (yearCount < params.minYearSpan || yearCount > params.maxYearSpan) {
        return of(null);
      }
      return this.compilingApiService
        .getPefSchema(
          params.tenderFormAnswerId,
          params.startYear,
          params.endYear,
        )
        .pipe(
          catchError((err) =>
            throwError(
              () =>
                new Error(
                  err?.error?.message ??
                    err?.message ??
                    'Errore nel caricamento dello schema PEF',
                ),
            ),
          ),
        );
    },
  });

  readonly pefDataResource = rxResource({
    params: () => ({
      tenderFormAnswerId: this.pefQuery().tenderFormAnswerId,
      startYear: this.pefQuery().startYear,
      endYear: this.pefQuery().endYear,
      minYearSpan: this.pefQuery().minYearSpan,
      maxYearSpan: this.pefQuery().maxYearSpan,
      pefVersion: this.pefQuery().pefVersion,
      userInputs: this.pefState.pefCalcInput(),
    }),
    stream: ({ params }) => {
      if (!params.tenderFormAnswerId || !params.startYear || !params.endYear) {
        return of(null);
      }
      const yearCount = pefYearSpan(
        params.startYear,
        params.endYear,
        params.pefVersion,
      );
      if (yearCount < params.minYearSpan || yearCount > params.maxYearSpan) {
        return of(null);
      }
      return this.compilingApiService
        .calculatePef(
          params.tenderFormAnswerId,
          params.startYear,
          params.endYear,
          params.userInputs ?? {},
        )
        .pipe(
          catchError((err) =>
            throwError(
              () =>
                new Error(
                  err?.error?.message ??
                    err?.message ??
                    'Errore nel calcolo del PEF',
                ),
            ),
          ),
        );
    },
  });

  readonly pefData = computed<PefData>(() => {
    if (this.pefDataResource.error()) return {};
    const value = this.pefDataResource.value();
    return value ? flattenPefApiData(value) : {};
  });

  private readonly safePefValue = computed<PefApiData | null | undefined>(
    () => {
      if (this.pefDataResource.error()) return undefined;
      return this.pefDataResource.hasValue()
        ? this.pefDataResource.value()
        : undefined;
    },
  );

  private readonly pefApiData$ = merge(
    toObservable(this.safePefValue),
    toObservable(this.pefDataResource.error).pipe(
      skip(1),
      filter((err): err is Error => err != null),
      switchMap((err) => throwError(() => err)),
    ),
  );

  constructor() {
    effect(() => {
      const version = this.pefState.pefSavedVersion();
      if (version > 0) {
        untracked(() => this.pefEditor?.clearDirty());
      }
    });

    effect(() => {
      const err = this.schemaResource.error();
      if (!err) this.schemaResource.value();
      this.activeSheet.set(0);
    });

    migrateUnknownAttachments(
      this.attachments.envelopeCAttachments,
      this.knownSlotIds,
    );
  }

  onNavClick(event: SidebarNavClickEvent): void {
    if (event.item.id === SUMMARY_ID) {
      this.activeView.set('summary');
      return;
    }
    if (event.item.id === ECONOMIC_OFFER_ID) {
      if (this.mode() === 'full') {
        this.activeView.set('economic-offer');
      }
      return;
    }
    if (event.item.id === DOCUMENTS_ID) {
      this.activeView.set('documents');
      return;
    }
    if (event.item.id === PEF_REPORT_ID) {
      this.activeView.set('pef-report');
      return;
    }
    const sheetId = event.child?.id ?? event.item.id;
    if (sheetId.startsWith(PEF_SHEET_PREFIX)) {
      const index = parseInt(sheetId.slice(PEF_SHEET_PREFIX.length), 10);
      this.activeSheet.set(index);
      this.activeView.set('pef');
    }
  }

  onYearRangeApply(event: YearRangeApplyEvent): void {
    this.pefState.pefStartYear.set(event.startYear);
    this.pefState.pefEndYear.set(event.endYear);
    this.pefState.pefInput.set(null);
    this.pefState.pefCalcInput.set(null);
  }

  /**
   * Writes the canone offered from the economic-offer view onto every year of
   * the concession period and recalculates: the value shown there comes from
   * the calculated PEF, so a plain input update would leave it stale.
   */
  onCanoneApply(value: number): void {
    const start = this.pefState.pefStartYear();
    const end = this.pefState.pefEndYear();
    if (start == null || end == null || end < start) return;

    const inputs = flattenPefApiData(this.pefState.pefInput() ?? {});
    for (let year = start; year <= end; year++) {
      inputs[`${CANONE_PEF_BASE_KEY}.${year}`] = value;
    }
    const structured = structurePefApiData(inputs);
    this.pefState.pefInput.set(structured);
    this.pefState.pefCalcInput.set(structured);
  }

  /**
   * Persists every cell edit into pefInput so a save (without an explicit
   * recalculation) still sends the latest values. Does not touch pefCalcInput,
   * so editing alone never triggers a server recalc.
   */
  onEditorInputsChange(inputs: PefApiData): void {
    this.pefState.pefInput.set(inputs);
  }

  load(): Observable<PefLoadResult> {
    this.guardInit();
    const currentError = this.pefDataResource.error();
    if (currentError) return throwError(() => currentError);
    const savedUserInputs = this.pefState.pefInput() ?? {};
    return this.pefApiData$.pipe(
      filter((data): data is PefApiData => data != null),
      take(1),
      map((data) => ({ data, savedUserInputs })),
    );
  }

  calculate(userInputs: PefApiData): Observable<PefApiData> {
    this.guardInit();
    const previous = this.pefDataResource.hasValue()
      ? this.pefDataResource.value()
      : undefined;
    this.pefState.pefInput.set(userInputs);
    this.pefState.pefCalcInput.set(userInputs);
    return this.pefApiData$.pipe(
      filter((data): data is PefApiData => data != null && data !== previous),
      take(1),
    );
  }

  private guardInit() {
    if (!this.pefQuery().tenderFormAnswerId)
      throw new Error('TenderFormAnswerId is not set.');
    if (this.pefQuery().startYear === null)
      throw new Error('StartYear is not set.');
    if (this.pefQuery().endYear === null)
      throw new Error('EndYear is not set.');
  }
}
